<template>
  <block-item-card
    v-if="marginSpreadSensitivity"
    :min-height="minBlockHeight"
    :title="$t('Rates.Section.MarginSpread.Title')"
    :title-info="$t('Rates.Section.MarginSpread.TitleInfo')"
    :tooltip="$t('Rates.Section.MarginSpread.MarginSpreadSensitivity')"
  >
    <v-row
      class="ma-0 py-4 px-2"
      no-gutters
    >
      <v-col
        cols="12"
        class="rounded-lg"
      >
        <div class="chart-container">
          <div class="chart-metric">
            <div
              class="formula"
              :style="`--const:'${spreadConstant}%'; --coef:'${Math.abs(sofrFactor)}%';` "
            >
              <span class="sym">Spread</span><sub>t</sub>
              <span>=</span>
              <span class="var-const" />
              <span>&minus;</span>
              <span class="var-coef" />
              <span>&times;</span>
              <span class="sym">{{ assetLabel }}</span><sub>t&minus;1</sub>
            </div>
          </div>
          <v-lazy
            :options="{threshold: 0.5}"
            transition="fade-transition"
          >
            <e-chart
              :ref="`echart-margin-spread`"
              :autoresize="true"
              :option="marginSpreadChartOptions"
              :style="`height: 350px;`"
              class="chart"
            />
          </v-lazy>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import {  toDecimalSignificant, computeYAxisBounds, toDecimalMark } from '@/lib/utils/helper'

use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

export default {
  name      : 'MarginSpread',
  components: {
    BlockItemCard
  },
  dataStore: { FindBankRegressionData: 'Rates.FindBankRegressionData.Data' },
  mixins   : [chartsColorMixin],
  props    : {},
  data () {
    return {
      minBlockHeight: 350
    }
  },
  computed: {
    marginSpreadSensitivity () {
      return this.FindBankRegressionData?.bank?.marginSpreadSensitivity
    },
    marginSpreadDates () {
      return this.marginSpreadSensitivity?.dates || []
    },
    marginSpread () {
      return this.marginSpreadSensitivity?.marginSpread || []
    },
    sofrFactor () {
      return this.marginSpreadSensitivity?.sofrFactor || 0
    },
    spreadConstant () {
      return this.marginSpreadSensitivity?.spreadConstant || 0
    },
    assetLabel () {
      return this.marginSpreadSensitivity?.assetLabel || ''
    },
    formattedDates () {
      if (!this.marginSpreadDates) return []

      return this.marginSpreadDates.map(item => {
        const d = new Date(item)
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const year = d.getFullYear().toString()
        return `${month} ${year}`
      })
    },

    marginSpreadChartOptions () {
      if (!this.marginSpread) return {}

      return {
        animation        : true,
        animationDuration: 1000,
        animationEasing  : 'quinticOut',
        tooltip          : {
          show           : true,
          trigger        : 'axis',
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : (params) => {
            if (!params[0].value || params[0].value === '-') {
              return null // disables tooltip + pointer
            }
            const value = params[0].value
            const axisValue = params[0].axisValue
            return `<div style="margin-bottom: 8px;">${axisValue}</div>
                      <div style="margin: 4px 0;">
                          <span style="font-weight: 700;">${toDecimalSignificant(value, 3)}%</span>
                      </div>`
          }
        },
        legend: { show: false },
        grid  : {
          left        : 65,
          top         : 50,
          right       : 30,
          bottom      : 0,
          containLabel: true
        },
        xAxis: {
          type     : 'category',
          data     : this.formattedDates,
          axisLabel: {
            margin  : 30,
            show    : true,
            color   : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize: 12

          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-06', 0.3),
              type : 'dashed'
            }
          }
        },
        yAxis: {
          type     : 'value',
          min      : computeYAxisBounds(this.marginSpread).min,
          max      : computeYAxisBounds(this.marginSpread).max,
          interval : computeYAxisBounds(this.marginSpread).interval,
          axisLabel: {
            margin       : -55,
            show         : true,
            color        : this.themeColorRGBA('synthesis-ui-grey-01'),
            fontSize     : 11,
            inside       : true,
            verticalAlign: 'bottom',
            formatter    : (value, index) => {
              return toDecimalMark(value) + '%'
            }
          },
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: {
            show     : true,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02', 1),
              type : 'dashed',
              width: 2
            }
          }
        },
        series: [{
          name     : 'Margin Spread',
          type     : 'line',
          data     : this.marginSpread,
          itemStyle: {
            color: this.themeColorRGBA('synthesis-ui-purple-02')
          },
          lineStyle: {
            width: 3
          },
          symbol    : 'emptyCircle',
          symbolSize: 0
        }]
      }
    }
  },
  watch  : {},
  mounted () {},
  created () {},
  methods: {}
}
    </script>

    <style scoped>

  .gradient-grey-06-bg {
    background-color: var(--v-synthesis-ui-grey-06-base) !important;
  }

  .chart-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }

  .chart-subtitle {
    margin-left: 8px;
    color: var(--v-synthesis-ui-grey-01-base);
    font-size: 10px;
    position: relative;
    top: 1px;
  }

  .metric-label {
    font-size: 12px;
    color: var(--v-synthesis-brand-dark-blue-base);
    margin-bottom: 2px;
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0 8px;
  }

  .chart-metric {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: -50px;
  }

  .metric-value {
    font-size: 12px;
    font-weight: 500;
  }

  .metric-value.positive {
    color: #4CAF50;
    font-size: 28px;
  }

  .metric-value.negative {
    color: #FF5722;
    font-size: 28px;
  }

  .chart-legend {
    display: flex;
    gap: 12px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #666;
  }

  .legend-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }

.formula{
  display:flex; align-items:baseline; gap:5px;
  font-weight: 500;
}
.formula sub{
  font-size:.6em; line-height:0; vertical-align:baseline; position:relative; top:.25em;
  margin-left: -5px;
}
.var-const::before{ content: var(--const); }
.var-coef::before{ content: var(--coef); }

</style>
